import type React from "react";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { getColours } from "@helpers/colours";

declare global {
	namespace JSX {
		interface IntrinsicElements {
			"veritree-map": React.DetailedHTMLProps<
				React.HTMLAttributes<HTMLElement> & {
					"org-id"?: string;
					"mapbox-token"?: string;
					height?: number | string;
					theme?: "light" | "dark" | "system";
					"details-view"?: "panel" | "popup";
					"cooperative-gestures"?: string;
					accent?: string;
					"on-accent"?: string;
					"accent-text"?: string;
					"api-url"?: string;
					"api-version"?: string;
				},
				HTMLElement
			>;
		}
	}
}

interface VeritreeMapProps {
	mapboxToken: string;
	name?: string;
	theme?: "light" | "dark" | "system";
	detailsView?: "panel" | "popup";
	cooperativeGestures?: boolean;
	accent?: string;
	onAccent?: string;
	accentText?: string;
	title?: string;
	subtitle?: string;
	background?: string;
}

const Wrapper = styled.section<{ $background?: string }>`
	padding: 0 0 40px 0;
	background-color: ${({ $background }) => $background || "transparent"};

	@media (min-width: 834px) {
		padding: 0 10% 60px 10%;
	}
`;

const Header = styled.h2`
	text-align: center;
	margin-bottom: 8px;
`;

const SubtitleText = styled.p`
	text-align: center;
	margin-bottom: 32px;
	color: #121252;
`;

const VeritreeMap: React.FC<VeritreeMapProps> = ({
	mapboxToken,
	theme,
	detailsView,
	cooperativeGestures = true,
	accent,
	onAccent,
	accentText,
	title,
	subtitle,
	background,
}) => {
	const [mapHeight, setMapHeight] = useState(900);

	useEffect(() => {
		const updateMapHeight = () =>
			setMapHeight(Math.round(window.innerHeight * 0.7));

		updateMapHeight();
		window.addEventListener("resize", updateMapHeight);

		return () => window.removeEventListener("resize", updateMapHeight);
	}, []);

	return (
		<Wrapper $background={background ? getColours(background) : undefined}>
			{title && <Header>{title}</Header>}
			{subtitle && <SubtitleText>{subtitle}</SubtitleText>}
			<veritree-map
				style={{ borderRadius: "16px", overflow: "hidden" }}
				height={mapHeight}
				org-id="XKWNJZM4TN3JZ3Z3"
				mapbox-token={mapboxToken}
				theme={theme}
				details-view={detailsView}
				cooperative-gestures={cooperativeGestures ? undefined : "false"}
				accent={accent}
				on-accent={onAccent}
				accent-text={accentText}
			/>
		</Wrapper>
	);
};

export default VeritreeMap;
